/**
 * Utility functions for validating route parameters
 * Used to prevent API calls with invalid clinic/branch parameters
 */

/**
 * Validates clinic and branch URL parameters
 * @param {string} clinicUrl - The clinic URL parameter
 * @param {string} branchUrl - The branch URL parameter
 * @param {string} actionName - Name of the action for logging (optional)
 * @returns {boolean} True if parameters are valid, false otherwise
 */
export const validateClinicBranchParams = (clinicUrl, branchUrl, actionName = 'API call') => {
  // Check for undefined, null, empty string, or literal 'undefined'/'null' strings
  if (!clinicUrl || !branchUrl || 
      clinicUrl === 'undefined' || branchUrl === 'undefined' ||
      clinicUrl === 'null' || branchUrl === 'null') {
    const error = new Error(`Invalid clinic/branch parameters for ${actionName}: clinicUrl=${clinicUrl}, branchUrl=${branchUrl}`);
    error.code = 'INVALID_PARAMS';
    console.error(`[Route Validation] ${error.message}`);
    return false;
  }
  
  // Format validation removed to allow any URL format including spaces and special characters
  
  // Check parameter length (reasonable limits)
  if (clinicUrl.length > 50 || branchUrl.length > 50) {
    const error = new Error(`Clinic/branch parameter too long for ${actionName}: clinicUrl=${clinicUrl}, branchUrl=${branchUrl}`);
    error.code = 'INVALID_LENGTH';
    console.error(`[Route Validation] ${error.message}`);
    return false;
  }
  
  return true;
};

/**
 * Validates route parameters from Vue router
 * @param {Object} routeParams - Vue router params object
 * @param {string} actionName - Name of the action for logging (optional)
 * @returns {boolean} True if parameters are valid, false otherwise
 */
export const validateRouteParams = (routeParams, actionName = 'Route navigation') => {
  if (!routeParams) {
    console.error(`[Route Validation] No route parameters provided for ${actionName}`);
    return false;
  }
  
  return validateClinicBranchParams(routeParams.clinicUrl, routeParams.branchUrl, actionName);
};

/**
 * Creates a safe payload with validated clinic/branch parameters
 * @param {Object} payload - Original payload object
 * @param {string} actionName - Name of the action for logging (optional)
 * @returns {Object|null} Validated payload or null if validation fails
 */
export const createSafePayload = (payload, actionName = 'API call') => {
  if (!payload) {
    console.error(`[Route Validation] No payload provided for ${actionName}`);
    return null;
  }
  
  if (!validateClinicBranchParams(payload.clinicUrl, payload.branchUrl, actionName)) {
    return null;
  }
  
  return payload;
};

/**
 * Wrapper function for API calls that require clinic/branch validation
 * @param {Function} apiCall - The API call function to execute
 * @param {Object} payload - Payload containing clinicUrl and branchUrl
 * @param {string} actionName - Name of the action for logging (optional)
 * @returns {Promise} Promise that resolves with API result or rejects with validation error
 */
export const safeApiCall = (apiCall, payload, actionName = 'API call') => {
  const safePayload = createSafePayload(payload, actionName);
  
  if (!safePayload) {
    return Promise.reject(new Error(`Invalid clinic/branch parameters for ${actionName}`));
  }
  
  return apiCall(safePayload);
};

/**
 * Checks if current route has valid clinic/branch context
 * @param {Object} route - Vue router route object
 * @returns {boolean} True if route has valid clinic/branch context
 */
export const hasValidClinicContext = (route) => {
  if (!route || !route.params) {
    return false;
  }
  
  return validateRouteParams(route.params, 'Route context check');
};

/**
 * Error types for route validation
 */
export const VALIDATION_ERRORS = {
  INVALID_PARAMS: 'INVALID_PARAMS',
  INVALID_FORMAT: 'INVALID_FORMAT', 
  INVALID_LENGTH: 'INVALID_LENGTH',
  NO_CLINIC_CONTEXT: 'NO_CLINIC_CONTEXT'
};